import React, { useState, useRef, useEffect } from "react";
import { useI18n } from "../context/I18nContext";
import { API } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export function Chatbot() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.session_id) sessionRef.current = payload.session_id;
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I had trouble responding. Please try the contact form!" };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="chatbot-toggle-btn"
        className="fixed bottom-5 right-5 z-[60] h-16 w-16 rounded-full bg-coral hover:bg-coral-dark text-white shadow-[0_10px_40px_rgba(224,109,83,0.5)] flex items-center justify-center transition-transform hover:scale-105 active:scale-95">
        {open ? <X className="h-7 w-7" /> : <MessageCircle className="h-7 w-7" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22 }} data-testid="chatbot-window"
            className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-[380px] h-[540px] max-h-[75vh] bg-cream rounded-3xl shadow-2xl border border-black/10 flex flex-col overflow-hidden">
            <div className="bg-navy text-white p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-mustard flex items-center justify-center text-navy font-bold"><Sparkles className="h-5 w-5" /></div>
              <div>
                <div className="font-heading font-semibold">{t.chat.title}</div>
                <div className="text-xs text-cream/70">Ainalem Assistant · 24/7</div>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
              <Bubble role="assistant" content={t.chat.greeting} />
              {messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content || (m.role === "assistant" && sending && i === messages.length - 1 ? "…" : "")} />)}
            </div>
            <div className="p-3 border-t border-black/10 flex gap-2 bg-white">
              <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t.chat.placeholder} data-testid="chatbot-input" className="rounded-full" />
              <Button onClick={send} disabled={sending} size="icon" className="rounded-full bg-coral hover:bg-coral-dark shrink-0" data-testid="chatbot-send-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${isUser ? "bg-navy text-white rounded-br-md" : "bg-white border border-black/5 text-navy rounded-bl-md"}`}>
        {content}
      </div>
    </div>
  );
}
